import { Button } from '@journ/ui/button'
import { cn } from '@journ/ui/cn'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import type { ApprovalStage, TimeOffType } from '../domain/timeoff'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import { TimeOffFilterList } from './time-off-filter-list'
import { useMemberParams } from './use-member-params'

const STAGES: ApprovalStage[] = ['pending_dept', 'pending_hr', 'approved', 'rejected']
const TYPES: TimeOffType[] = ['vacation', 'sick_leave', 'unpaid_leave', 'parental_leave', 'other']

const TimeOffHeader = () => {
	const content = useIntlayer('hr-page')
	const to = content.timeOff
	const { q, toStage, toType, setParams } = useMemberParams()
	const [isOpen, setIsOpen] = useState(false)
	const canRequest = usePermission('hr.time-off.request')

	const hasFilters = Boolean(toStage || toType)

	return (
		<div className='flex items-center justify-end gap-4'>
			<div />
			<div className='flex items-center gap-2'>
				<TimeOffFilterList />
				<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
					<div className='relative'>
						<Icons.Search size={16} className='absolute pointer-events-none left-3 top-2.75 text-muted-foreground' />
						<Input
							placeholder={to.searchPlaceholder.value}
							className='pl-9 w-full md:w-87.5 pr-8'
							value={q ?? ''}
							onChange={e => setParams({ q: e.target.value || null })}
							autoComplete='off'
							autoCapitalize='none'
							autoCorrect='off'
							spellCheck={false}
						/>
						<DropdownMenuTrigger asChild>
							<button
								type='button'
								className={cn(
									'absolute z-10 right-3 top-2.5 opacity-50 transition-opacity duration-300 hover:opacity-100',
									hasFilters ? 'opacity-100' : undefined,
									isOpen ? 'opacity-100' : undefined,
								)}
							>
								<Icons.Filter size={16} />
							</button>
						</DropdownMenuTrigger>
					</div>

					<DropdownMenuContent className='w-55' align='end' sideOffset={10}>
						<DropdownMenuGroup>
							<DropdownMenuSub>
								<DropdownMenuSubTrigger>
									<Icons.ProjectStatus className='mr-2 size-4 rotate-180' />
									<span>{to.columns.status}</span>
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent sideOffset={14} alignOffset={-4}>
										{STAGES.map(s => (
											<DropdownMenuCheckboxItem
												key={s}
												checked={toStage === s}
												onSelect={e => e.preventDefault()}
												onCheckedChange={() => setParams({ toStage: toStage === s ? null : s })}
											>
												{to.stage[s]}
											</DropdownMenuCheckboxItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
						</DropdownMenuGroup>

						<DropdownMenuGroup>
							<DropdownMenuSub>
								<DropdownMenuSubTrigger>
									<Icons.Status className='mr-2 size-4' />
									<span>{to.columns.type}</span>
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent sideOffset={14} alignOffset={-4}>
										{TYPES.map(t => (
											<DropdownMenuCheckboxItem
												key={t}
												checked={toType === t}
												onSelect={e => e.preventDefault()}
												onCheckedChange={() => setParams({ toType: toType === t ? null : t })}
											>
												{to.requestType[t]}
											</DropdownMenuCheckboxItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
						</DropdownMenuGroup>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			<div className='flex items-center gap-2'>
				{canRequest && (
					<Button variant='outline' size='icon' onClick={() => setParams({ createTimeOff: true })}>
						<Icons.Add size={16} />
					</Button>
				)}
			</div>
		</div>
	)
}

export { TimeOffHeader }
